/**
 * Provider Form Dialog
 *
 * Modal dialog for creating or editing a login provider. Fields below the
 * name/display_name/enabled/type row are conditional on `type` (and, for
 * `oidc`, on `acquisition_mode`) — see OidcProviderFields/StaticProviderFields.
 */

import { useState, useEffect, type ReactElement, type FormEvent } from 'react';
import type { Provider, ProviderRequestBody } from '../../types';
import type { ProviderFormData } from './providerFormTypes';
import { CommonProviderFields } from './CommonProviderFields';
import { OidcProviderFields } from './OidcProviderFields';
import { StaticProviderFields } from './StaticProviderFields';
import { buildProviderRequestBody } from './buildProviderRequestBody';

const EMPTY_FORM: ProviderFormData = {
  name: '',
  display_name: '',
  enabled: true,
  type: 'oidc',
  acquisition_mode: 'direct',
  broker_mechanism: null,
  broker_alias: null,
  issuer_url: null,
  authorization_endpoint: null,
  token_endpoint: null,
  client_id: null,
  client_secret: '',
  scopes: null,
  secret_label: null,
  help_text: null,
  github_app_kind: null,
};

function brokerMechanismOf(
  value: string | null,
): 'keycloak-broker' | 'token-exchange' | null {
  return value === 'keycloak-broker' || value === 'token-exchange' ? value : null;
}

interface ProviderFormDialogProps {
  open: boolean;
  provider: Provider | null;
  onClose: () => void;
  onSubmit: (body: ProviderRequestBody) => void;
  isSubmitting: boolean;
  submitError: string | null;
}

export function ProviderFormDialog({
  open,
  provider,
  onClose,
  onSubmit,
  isSubmitting,
  submitError,
}: ProviderFormDialogProps): ReactElement | null {
  const [form, setForm] = useState<ProviderFormData>(EMPTY_FORM);
  const [extraAuthParamsJson, setExtraAuthParamsJson] = useState('{}');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const isEdit = provider !== null;

  useEffect(() => {
    if (provider) {
      setForm({
        name: provider.name,
        display_name: provider.display_name,
        enabled: provider.enabled,
        type: provider.type === 'static' ? 'static' : 'oidc',
        acquisition_mode: provider.acquisition_mode === 'brokered' ? 'brokered' : 'direct',
        broker_mechanism: brokerMechanismOf(provider.broker_mechanism),
        broker_alias: provider.broker_alias,
        issuer_url: provider.issuer_url,
        authorization_endpoint: provider.authorization_endpoint,
        token_endpoint: provider.token_endpoint,
        client_id: provider.client_id,
        client_secret: '',
        scopes: provider.scopes,
        secret_label: provider.secret_label,
        help_text: provider.help_text,
        github_app_kind: provider.github_app_kind,
      });
      setExtraAuthParamsJson(JSON.stringify(provider.extra_auth_params ?? {}, null, 2));
    } else {
      setForm(EMPTY_FORM);
      setExtraAuthParamsJson('{}');
    }
    setJsonError(null);
  }, [provider, open]);

  if (!open) return null;

  const setField = <K extends keyof ProviderFormData>(
    key: K,
    value: ProviderFormData[K],
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setJsonError(null);
    let extraAuthParams: Record<string, unknown> | null = null;
    if (form.type === 'oidc' && form.acquisition_mode === 'direct') {
      try {
        const parsed = JSON.parse(extraAuthParamsJson) as Record<string, unknown>;
        extraAuthParams = Object.keys(parsed).length > 0 ? parsed : null;
      } catch {
        setJsonError('Invalid JSON in extra auth params');
        return;
      }
    }
    onSubmit(buildProviderRequestBody(form, extraAuthParams));
  };

  return (
    <div className="dialog-overlay" onClick={onClose} role="presentation">
      <dialog
        className="dialog"
        open
        onClick={(e) => { e.stopPropagation(); }}
        aria-label={isEdit ? 'Edit login provider' : 'Create login provider'}
      >
        <h2>{isEdit ? 'Edit Login Provider' : 'Create Login Provider'}</h2>
        <form onSubmit={handleSubmit} className="dialog__form">
          <CommonProviderFields form={form} setField={setField} isEdit={isEdit} />

          {form.type === 'oidc' && (
            <OidcProviderFields
              form={form}
              setField={setField}
              hasClientSecret={provider?.has_client_secret ?? false}
              extraAuthParamsJson={extraAuthParamsJson}
              onExtraAuthParamsChange={setExtraAuthParamsJson}
            />
          )}
          {form.type === 'static' && (
            <StaticProviderFields form={form} setField={setField} />
          )}

          {jsonError && (
            <div className="dialog__error" role="alert">
              {jsonError}
            </div>
          )}
          {submitError && (
            <div className="dialog__error" role="alert">
              {submitError}
            </div>
          )}

          <div className="dialog__actions">
            <button
              type="button"
              onClick={onClose}
              className="dialog__btn dialog__btn--secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dialog__btn dialog__btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </dialog>
    </div>
  );
}
